<script setup lang="ts">
import { ref, watch, useTemplateRef } from 'vue'
import { storeToRefs } from 'pinia'
import {
  NXGDSButton,
  NXGDSButtonVariants,
  NXGDSModal,
  NXGDSTextField,
} from '@nsftx/seven-nxg-design-system'
import { isVoucher, ScanService } from '@nsftx/seven-client-retail-utils'
import { useSlipCheckStore } from '@/stores/slip-check/slipCheckStore'
import { checkAndShowSlipDetails } from '@/modules/slip-check/slipCheckService'
import { checkAndShowVoucherDetails } from '@/modules/voucher/voucherPOSService'

const isLoading = ref(false)
const slipIdInput = useTemplateRef('slipIdInput')
const { isSlipCheckFormOpened, slipId } = storeToRefs(useSlipCheckStore())

watch(slipIdInput, (input) => {
  if (input && isSlipCheckFormOpened.value) {
    input.focusAndScrollIntoView()
  }
})

const handleSlipCheck = async (slipId: string) => {
  try {
    isLoading.value = true
    if (isVoucher(slipId)) {
      await checkAndShowVoucherDetails(slipId)
    } else {
      await checkAndShowSlipDetails(slipId, true)
    }
  } finally {
    isLoading.value = false
  }
}

const handleSlipKeydown = (e: KeyboardEvent) => {
  if (ScanService().shouldPropagateScanKey(e)) {
    return
  }

  e.stopPropagation()

  if (e.key === 'Enter' && slipId.value) {
    slipId.value = slipId.value.replace(/ /g, '')
    handleSlipCheck(slipId.value)
  }
}
</script>

<template>
  <NXGDSModal
    v-model="isSlipCheckFormOpened"
    :title="$t('slip_check_modal_title')"
    show-close-button
    close-on-outside-click
  >
    <div class="slip-check-modal__body">
      <p class="slip-check-modal__content">{{ $t('slip_check_modal_content') }}</p>

      <NXGDSTextField
        v-model="slipId"
        name="slipId"
        :label="$t('slip_check_modal_ticket_id_label')"
        ref="slipIdInput"
        big
        @keydown="handleSlipKeydown"
      />
    </div>

    <template #footer>
      <NXGDSButton
        :loading="isLoading"
        @click="handleSlipCheck(slipId!)"
        :variant="NXGDSButtonVariants.primary"
        width="100%"
      >
        {{ $t('slip_check_modal_check_button') }}
      </NXGDSButton>
    </template>
  </NXGDSModal>
</template>

<style scoped lang="scss">
@use '@nsftx/seven-nxg-design-system/style/mixins' as *;

.slip-check-modal__body {
  display: flex;
  flex-direction: column;
  gap: var(--nxgds-alias-spacing-md);
}

.slip-check-modal__content {
  @include typography('paragraph', 'big-regular');
  color: var(--nxgds-mapped-color-text-secondary);
}
</style>
